import { ItemCategory } from "../constants/itemCategory";
import { InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import clsx from "clsx";
import { HeartPulseIcon, PizzaIcon } from "@/shared/icons";
import { ItemRarity } from "@/domains/inventory/constants/itemRarity";
import accentColorByRarity from "@/domains/inventory/helpers/accentColorByRarity";
import translateRarity from "@/domains/inventory/helpers/translateRarity";
import InventoryBorderBox from "@/domains/inventory/components/InventoryBorderBox";

function Wear({ percent }: { percent: number }) {
  return (
    <div className="relative h-[1.5rem] w-[10rem] mt-1.5">
      <div
        className={clsx(
          "absolute w-full h-full border-2 border-[#84FF97]/70",
          "bg-[#84FF97]/50 rounded-[0.375rem]",
        )}
        style={{
          WebkitMaskImage: "linear-gradient(to right, #00000040 0%, #00000020 40%, transparent)",
        }}
      />
      <div className="absolute w-full h-full flex items-center gap-1 px-2 text-12 text-[#84FF97] font-medium">
        <span className="font-bold">{percent}%</span> Процент износа
      </div>
    </div>
  );
}

function Charges({ count }: { count: number }) {
  return (
    <div className="relative h-[1.5rem] w-[10rem] mt-1.5">
      <div
        className={clsx(
          "absolute w-full h-full border-2 border-accent-solid/70",
          "bg-accent-solid/50 rounded-[0.375rem]",
        )}
        style={{
          WebkitMaskImage: "linear-gradient(to right, #00000040 0%, #00000020 40%, transparent)",
        }}
      />
      <div
        className={clsx(
          "absolute w-full h-full flex items-center gap-1 px-2 text-12 text-accent-solid font-medium",
        )}
      >
        <span className="font-bold">{count}</span> Количество зарядов
      </div>
    </div>
  );
}

export default function InventoryTooltip({
  weight,
  itemTemplate,
  details,
  quantity,
  attachedItems,
  wear,
  ...props
}: InventoryItem) {
  const color = accentColorByRarity[itemTemplate.rarity || ItemRarity.Common];

  const weapon = (
    <>
      {details?.weapon && (
        <>
          <div className="mt-1.5">
            <div className="text-14 font-medium gap-1">Серийный номер:</div>
            <div className="flex items-center gap-1.5">
              <div className="w-[5px] h-[5px] rounded-full bg-accent-solid shrink-0" />
              <div className="text-13 font-bold text-accent-solid">
                {details?.weapon.serialNumber}
              </div>
            </div>
          </div>
          {!!attachedItems?.length && (
            <div className="flex flex-col mt-1.5 text-13 font-medium">
              <div>Улучшения:</div>
              {attachedItems.map((item) => (
                <div className="flex items-center gap-1.5" key={item.id}>
                  <div className="text-accent-solid">-</div>
                  {item.itemTemplate.name}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <div className="w-full h-[1px] bg-dark-700 my-2.5" />
      <div className="w-fit text-13 text-white/60 font-medium p-2 rounded-[0.375rem] border border-dark-700">
        Тип оружия: <span className="text-white/80">{itemTemplate.type}</span>
      </div>
      {wear && <Wear percent={wear} />}
    </>
  );

  const products = (
    <div className="flex items-center gap-1.5 mt-1.5">
      <PizzaIcon size={16} className="text-accent-solid" />
      <div className="text-14 font-medium">
        Сытость:{" "}
        <span className="text-13 font-bold text-accent-solid">
          {itemTemplate?.properties?.hunger}%
        </span>
      </div>
    </div>
  );

  const tech = (
    <>
      <div className="w-full h-[1px] bg-dark-700 my-2.5" />
      <div className="w-fit text-13 text-white/60 font-medium p-2 rounded-[0.375rem] border border-dark-700">
        Категория: <span className="text-white/80">{itemTemplate.type}</span>
      </div>
      <Charges count={0} />
    </>
  );

  const clothes = (
    <>
      <div className="w-full h-[1px] bg-dark-700 my-2.5" />
      <div className="w-fit text-13 text-white/60 font-medium p-2 rounded-[0.375rem] border border-dark-700">
        Тип: <span className="text-white/80">{itemTemplate.type}</span>
      </div>
      {wear && <Wear percent={wear} />}
    </>
  );

  const med = (
    <>
      <div className="flex items-center gap-1.5 mt-1.5">
        <HeartPulseIcon size={16} className="text-accent-solid" />
        <div className="text-14 font-medium">
          Сытость: <span className="text-13 font-bold text-accent-solid">?%</span>
        </div>
      </div>
      <div className="w-full h-[1px] bg-dark-700 my-2.5" />
      <div className="w-fit text-13 text-white/60 font-medium p-2 rounded-[0.375rem] border border-dark-700">
        Тип: <span className="text-white/80">{itemTemplate.type}</span>
      </div>
      <Charges count={0} />
    </>
  );

  return (
    <div className="w-fit min-w-[9rem] overflow-clip">
      {!!quantity && (itemTemplate.isStackable || itemTemplate.maxStack > 1) && (
        <div className="flex items-center justify-between mb-1.5">
          <div className="px-1 py-0.5 rounded-[0.25rem] bg-gradient-to-t from-white/30 to-transparent text-11 font-bold">
            {quantity} <span className="text-white/50">шт.</span>
          </div>
          <div className="text-14 font-medium text-white/30">Личный</div>
        </div>
      )}
      <div className="text-15 font-medium">{itemTemplate.name}</div>
      <div className="text-14 font-medium text-white/50 max-w-[15rem] whitespace-normal">
        {itemTemplate.description}
      </div>

      {itemTemplate.category === ItemCategory.Weapon && weapon}
      {itemTemplate.category === ItemCategory.Products && products}
      {itemTemplate.category === ItemCategory.Tech && tech}
      {itemTemplate.category === ItemCategory.Clothes && clothes}
      {itemTemplate.category === ItemCategory.Med && med}
      {itemTemplate.category === ItemCategory.Resources && null}
      {itemTemplate.category === ItemCategory.Capacity && null}
      {itemTemplate.category === ItemCategory.Quest && null}
      {itemTemplate.category === ItemCategory.WeaponComponents && null}

      {/* {details?.canister && (
        <>
          <div className="p-2 w-fit border border-[#242424] rounded-[0.375rem] text-13 font-medium text-white/60">
            Тип: <span className="text-white/80 capitalize">Канистра</span>
          </div>
          {details.canister.liquidType && (
            <div className="flex gap-1.5 items-center mt-1.5">
              <div className="w-[5px] h-[5px] rounded-full bg-accent-solid shrink-0" />
              <div className="text-14 font-medium capitalize">{details.canister.liquidType}</div>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-14 font-medium mt-1.5">
            Объем:{" "}
            <div>
              <span className="text-13 font-bold text-accent-solid">{details.canister.volume}</span>{" "}
              <span className="text-13 font-bold text-white/50">
                / {details.canister.maxVolume} L
              </span>
            </div>
          </div>
          <div className="text-14 font-medium mt-1.5">
            Качество:{" "}
            <span className="text-13 font-bold text-white/50">{details.canister.quality}</span>
          </div>
        </>
      )} */}
      <div className="flex items-end justify-between mt-1.5">
        <InventoryBorderBox className="p-2 rounded-[0.5rem]" color={color}>
          <div className="text-13 font-medium" style={{ color }}>
            {translateRarity[itemTemplate.rarity]}
          </div>
        </InventoryBorderBox>
        <p className="flex items-center gap-1 text-11 font-bold text-accent-solid">
          {weight}
          <span className="text-10 font-extrabold text-white">KG</span>
        </p>
      </div>
    </div>
  );
}
